import { Link, useNavigate, useRouterState } from '@tanstack/react-router'
import { Ellipsis, Search } from 'lucide-react'
import type { NavEntry } from '../../features/feature.ts'
import { useT } from '../../lib/i18n.tsx'
import { Sheet } from '../../kit/Sheet.tsx'
import { cn } from '../../kit/util.ts'
import { isActive, useNavEntries } from '../nav.ts'
import { closeOverlay, openOverlay, useOverlay } from '../ui.ts'
import { Preferences } from './UserMenu.tsx'

const tabClass = 'relative flex flex-1 flex-col items-center justify-center gap-1 pt-1.5 text-xs font-medium transition'

function Tab({ entry, pathname }: { entry: NavEntry; pathname: string }) {
  const t = useT()
  const active = isActive(pathname, entry.to)
  const Icon = entry.icon
  return (
    <Link to={entry.to} aria-current={active ? 'page' : undefined} className={cn(tabClass, active ? 'text-ink' : 'text-ink-faint')}>
      {active && <span aria-hidden className="absolute top-0 h-0.5 w-8 rounded-full bg-indicator" />}
      <Icon size={20} strokeWidth={active ? 2 : 1.75} aria-hidden className={active ? 'text-accent-text' : undefined} />
      {t(entry.label)}
    </Link>
  )
}

/** The rail entries that are tabs, in the approved mockup's order (DECISIONS, P3b, O4); Search sits after the first, More last. */
const TAB_PATHS = ['/today', '/pipeline', '/ask']

/** Phone bottom tab bar: Today, Search, Pipeline, Ask and More. Safe-area aware. */
export function TabBar({ search }: { search: boolean }) {
  const t = useT()
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const more = useOverlay('more')
  const navEntries = useNavEntries()
  const tabs = TAB_PATHS.flatMap((to) => navEntries.find((e) => e.to === to) ?? [])
  const [first, ...rest] = tabs
  return (
    <>
      <nav
        aria-label={t('nav.tabs')}
        className="fixed inset-x-0 bottom-0 z-40 flex h-tabbar-safe items-stretch border-t border-line bg-glass pb-safe backdrop-blur-md md:hidden"
      >
        {first && <Tab entry={first} pathname={pathname} />}
        {search && (
          <button type="button" onClick={() => openOverlay('omnibar')} className={cn(tabClass, 'text-ink-faint')}>
            <Search size={20} strokeWidth={1.75} aria-hidden />
            {t('nav.search')}
          </button>
        )}
        {rest.map((e) => (
          <Tab key={e.to} entry={e} pathname={pathname} />
        ))}
        <button type="button" onClick={() => openOverlay('more')} aria-expanded={more} className={cn(tabClass, more ? 'text-ink' : 'text-ink-faint')}>
          <Ellipsis size={20} strokeWidth={1.75} aria-hidden />
          {t('nav.more')}
        </button>
      </nav>
      <MoreSheet open={more} exclude={tabs} pathname={pathname} />
    </>
  )
}

function MoreSheet({ open, exclude, pathname }: { open: boolean; exclude: NavEntry[]; pathname: string }) {
  const t = useT()
  const navigate = useNavigate()
  const items = useNavEntries().filter((e) => !exclude.includes(e))
  const go = (to: string) => {
    closeOverlay()
    void navigate({ to })
  }
  return (
    <Sheet open={open} onClose={closeOverlay} side="bottom" title={t('nav.more')}>
      {items.length > 0 && (
        <div className="mb-5 grid grid-cols-3 gap-2">
          {items.map((e) => {
            const Icon = e.icon
            const on = isActive(pathname, e.to)
            return (
              <button
                key={e.to}
                type="button"
                onClick={() => go(e.to)}
                aria-current={on ? 'page' : undefined}
                className={cn(
                  'flex h-20 flex-col items-center justify-center gap-2 rounded-lg p-2 text-center text-xs font-medium ring-inset transition',
                  on ? 'bg-accent-soft text-ink ring-2 ring-indicator' : 'bg-raised text-ink-muted ring-1 ring-line hover:text-ink',
                )}
              >
                <Icon size={20} strokeWidth={1.75} aria-hidden />
                <span className="leading-tight">{t(e.label)}</span>
              </button>
            )
          })}
        </div>
      )}
      <div className="flex flex-col gap-1">
        <Preferences />
      </div>
    </Sheet>
  )
}
